import { inject, Inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { EmailNotificationsModel, EmailCustomerSummaryModel } from '../models/EmailNotifications-Model';

@Injectable({
    providedIn: 'root'
})
export class EmailNotificationsService {
    private url: string = `${this.API_URL}`;


    isLoading = signal<boolean>(false);
    public emailNotifications = signal<EmailNotificationsModel[]>([]);
    public customerSummary = signal<EmailCustomerSummaryModel[]>([]);


    isDateRangePickerModalOpened = signal<boolean>(false);

    public startDate: string = '';
    public endDate: string = '';
    public selectedTimeFrame = signal<string | undefined>('');
    public customerDetails = signal<EmailNotificationsModel[]>([]);
    public isCustomerDetailsLoading = signal<boolean>(false);


    pageNumber = signal<number>(0);
    pageSize = signal<number>(50);

    private datePipe = inject(DatePipe);

    constructor(@Inject('API_URL') private API_URL: string, private httpClient: HttpClient) {
    }


    resetEmailNotifications() {
        this.customerSummary.set([]);
        this.emailNotifications.set([]);
        this.pageNumber.set(0);
    }

    public getEmailNotifications() {
        this.isLoading.set(true);
        this.httpClient.get<{content: EmailCustomerSummaryModel[]}>(`${this.url}/email/winOls/all`, {
            params: {
                'start date': this.startDate,
                'end date': this.endDate,
                pageNumber: this.pageNumber(),
                pageSize: this.pageSize()
            }
        }).subscribe({
            next: (response) => {
                this.customerSummary.set(response['content']);
                this.isLoading.set(false);
            },
            error: (error) => {
                console.error('Error fetching email notifications:', error);
                this.isLoading.set(false);
            }
        });
    }


    getEmailNotificationsOnScroll() {
        this.isLoading.set(true);
        this.httpClient.get<{content:EmailCustomerSummaryModel[]}>(`${this.url}/email/winOls/all`, {
            params: {
                'start date': this.startDate,
                'end date': this.endDate,
                pageNumber: this.pageNumber(),
                pageSize: this.pageSize()
            }
        }).subscribe({
            next: (response) => {
                this.customerSummary.update((summary) => [...summary, ...response["content"]]);
                this.isLoading.set(false);
            },
            error: (error) => {
                console.error('Error fetching more email notifications:', error);
                this.isLoading.set(false);
            }
        });
    }

    e
    increasePageNumber() {
        this.pageNumber.update((prevNum) => prevNum + 1);
    }

    public getCustomerEmailDetails(customerNumber: number, email: string, startDate: string, endDate: string) {
        this.isCustomerDetailsLoading.set(true);

        this.httpClient.get<{ content: EmailNotificationsModel[] }>(`${this.url}/email/winOls/customers/${customerNumber}/emails`, {
                params: {
                    email: email,
                    'start date': startDate,
                    'end date': endDate,
                    pageNumber: this.pageNumber(),
                    pageSize: this.pageSize()
                }
            }
        ).subscribe({next: (response) => {
                this.customerDetails.set(response.content);
                this.isCustomerDetailsLoading.set(false);
            },
            error: (error) => {
                console.error('Error fetching customer email details:', error);
                this.customerDetails.set([]);
                this.isCustomerDetailsLoading.set(false);
            }
        });
    }

    openDateRangePickerModal() {
        this.isDateRangePickerModalOpened.set(true);
    }

    closeDateRangePickerModal($event: any) {
        this.isDateRangePickerModalOpened.set($event.valueOf());
    }

    async submitEmailFormDate($event: any) {
        const startDateFormValue = $event.valueOf().startDate;
        const endDateFormValue = $event.valueOf().endDate;
        const label = $event.valueOf().label;

        this.startDate = new Date(startDateFormValue).toISOString();
        this.endDate = new Date(endDateFormValue).toISOString();

        localStorage.setItem('emailStartDate', this.startDate);
        localStorage.setItem('emailEndDate', this.endDate);

        this.resetEmailNotifications();
        this.getEmailNotifications();

        if (label) {
            this.selectedTimeFrame.set(label);
        } else {
            const formattedStart = this.datePipe.transform(this.startDate, 'yyyy-MM-dd');
            const formattedEnd = this.datePipe.transform(this.endDate, 'yyyy-MM-dd');
            this.selectedTimeFrame.set(`${formattedStart} - ${formattedEnd}`);
        }
    }

    getTodayEmailNotifications() {
        this.endDate = new Date().toISOString();
        const d = new Date();
        d.setUTCHours(0, 0, 0, 0);
        this.startDate = d.toISOString();

        localStorage.setItem('emailStartDate', this.startDate);
        localStorage.setItem('emailEndDate', this.endDate);
        this.selectedTimeFrame.set('Today');
    }



    setTodayAsDefault() {
        this.getTodayEmailNotifications();
    }


    loadDatesFromStorage() {
        const storedStartDate = localStorage.getItem('emailStartDate');
        const storedEndDate = localStorage.getItem('emailEndDate');

        if (storedStartDate && storedEndDate) {
            this.startDate = storedStartDate;
            this.endDate = storedEndDate;

            const formattedStart = this.datePipe.transform(this.startDate, 'yyyy-MM-dd');
            const formattedEnd = this.datePipe.transform(this.endDate, 'yyyy-MM-dd');
            this.selectedTimeFrame.set(`${formattedStart} - ${formattedEnd}`);
        } else {
            this.setTodayAsDefault();
        }
    }


    refreshTableOnInit() {
        this.resetEmailNotifications();
        localStorage.removeItem('emailStartDate');
        localStorage.removeItem('emailEndDate');
        this.getTodayEmailNotifications();
        this.getEmailNotifications();
    }

}
